import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  Alert,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PublicListing, useSearch } from '@/contexts/SearchContext';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import ContactHostForm from './ContactHostForm';

interface BookingInquiryWidgetProps {
  listing: PublicListing;
}

interface InquiryFormData {
  checkInDate: Date | null;
  checkOutDate: Date | null;
  guestCount: number;
}

const BookingInquiryWidget: React.FC<BookingInquiryWidgetProps> = ({ listing }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { requireAuth, isAuthenticated } = useAuthRedirect();
  const { state: searchState } = useSearch();
  
  const [formData, setFormData] = useState<InquiryFormData>({
    checkInDate: null,
    checkOutDate: null,
    guestCount: 1,
  });
  
  const [showContactForm, setShowContactForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Prefill form data from search context when component mounts
  useEffect(() => {
    const { searchParams } = searchState;
    
    // Only prefill if we have search parameters and form is currently empty
    if (searchParams && (!formData.checkInDate && !formData.checkOutDate && formData.guestCount === 1)) {
      const checkInDate = searchParams.checkIn ? new Date(searchParams.checkIn) : null;
      const checkOutDate = searchParams.checkOut ? new Date(searchParams.checkOut) : null;
      const guestCount = searchParams.guests || 1;
      
      // Validate dates are not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const validCheckIn = checkInDate && checkInDate >= today ? checkInDate : null;
      const validCheckOut = checkOutDate && checkOutDate > (validCheckIn || today) ? checkOutDate : null;
      
      setFormData({
        checkInDate: validCheckIn,
        checkOutDate: validCheckOut,
        guestCount: Math.min(guestCount, listing.maxGuests),
      });
    }
  }, [searchState, listing.maxGuests, formData.checkInDate, formData.checkOutDate, formData.guestCount]);

  // Calculate pricing
  const calculatePricing = useCallback(() => {
    const { checkInDate, checkOutDate, guestCount } = formData;
    
    if (!checkInDate || !checkOutDate) {
      return null;
    }
    
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (nights <= 0) {
      return null;
    }
    
    const basePrice = listing.pricing.basePrice * nights;
    const cleaningFee = listing.pricing.cleaningFee || 0;
    const serviceFee = Math.round(basePrice * 0.14); // 14% service fee
    const taxes = Math.round((basePrice + serviceFee) * 0.08); // 8% taxes
    const total = basePrice + cleaningFee + serviceFee + taxes;
    
    return {
      nights,
      basePrice,
      cleaningFee,
      serviceFee,
      taxes,
      total,
    };
  }, [formData, listing.pricing]);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof InquiryFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle guest count changes
  const handleGuestCountChange = useCallback((increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      guestCount: increment 
        ? Math.min(prev.guestCount + 1, listing.maxGuests)
        : Math.max(prev.guestCount - 1, 1)
    }));
  }, [listing.maxGuests]);



  // Handle contact host button click
  const handleContactHost = useCallback(() => {
    requireAuth(() => {
      setShowContactForm(true);
      setSubmitSuccess(false);
    });
  }, [requireAuth]);

  // Handle successful form submission
  const handleFormSuccess = useCallback(() => {
    setSubmitSuccess(true);
    setShowContactForm(false);
    
    // Reset form after successful submission
    setTimeout(() => {
      setFormData({
        checkInDate: null,
        checkOutDate: null,
        guestCount: 1,
      });
      setSubmitSuccess(false);
    }, 3000);
  }, []);

  // Handle form close
  const handleCloseForm = useCallback(() => {
    setShowContactForm(false);
  }, []);

  const pricing = calculatePricing();
  const currencySymbol = listing.pricing.currency === 'USD' ? '$' : listing.pricing.currency;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card
        sx={{
          borderRadius: isMobile ? 2 : 3,
          boxShadow: isMobile ? theme.shadows[2] : theme.shadows[8],
          border: 1,
          borderColor: 'divider',
          position: isMobile ? 'static' : 'sticky',
          top: isMobile ? 'auto' : 24,
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Success Message */}
          <Collapse in={submitSuccess}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your inquiry has been sent! The host will contact you via WhatsApp soon.
            </Alert>
          </Collapse>

          {/* Pricing Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                {currencySymbol}{listing.pricing.basePrice}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / night
              </Typography>
            </Box>
            
            {listing.instantBook && (
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                Instant Book Available
              </Typography>
            )}
          </Box>

          {/* Date Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: isMobile ? 2 : 1, 
              mb: 2 
            }}>
              <DatePicker
                label="Check-in"
                value={formData.checkInDate}
                onChange={(date) => handleFieldChange('checkInDate', date)}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    size: isMobile ? 'medium' : 'small',
                    fullWidth: true,
                  },
                }}
              />
              
              <DatePicker
                label="Check-out"
                value={formData.checkOutDate}
                onChange={(date) => handleFieldChange('checkOutDate', date)}
                minDate={formData.checkInDate || new Date()}
                slotProps={{
                  textField: {
                    size: isMobile ? 'medium' : 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Box>

            {/* Guest Counter */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  Guests
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size={isMobile ? 'medium' : 'small'}
                  onClick={() => handleGuestCountChange(false)}
                  disabled={formData.guestCount <= 1}
                  sx={{
                    minWidth: isMobile ? 44 : 32,
                    minHeight: isMobile ? 44 : 32,
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    minWidth: 20, 
                    textAlign: 'center',
                    fontSize: isMobile ? '1.1rem' : '1rem',
                  }}
                >
                  {formData.guestCount}
                </Typography>
                
                <IconButton
                  size={isMobile ? 'medium' : 'small'}
                  onClick={() => handleGuestCountChange(true)}
                  disabled={formData.guestCount >= listing.maxGuests}
                  sx={{
                    minWidth: isMobile ? 44 : 32,
                    minHeight: isMobile ? 44 : 32,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            

          </Box>

          {/* Pricing Breakdown */}
          {pricing && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {currencySymbol}{listing.pricing.basePrice} × {pricing.nights} night{pricing.nights > 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2">
                  {currencySymbol}{pricing.basePrice}
                </Typography>
              </Box>
              
              {pricing.cleaningFee > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Cleaning fee
                  </Typography>
                  <Typography variant="body2">
                    {currencySymbol}{pricing.cleaningFee}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Service fee
                </Typography>
                <Typography variant="body2">
                  {currencySymbol}{pricing.serviceFee}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">
                  Taxes
                </Typography>
                <Typography variant="body2">
                  {currencySymbol}{pricing.taxes}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {currencySymbol}{pricing.total}
                </Typography>
              </Box>
            </Box>
          )}



          {/* Action Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleContactHost}
            disabled={!pricing}
            sx={{
              py: isMobile ? 2 : 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: isMobile ? '1.1rem' : '1rem',
              minHeight: isMobile ? 56 : 'auto',
            }}
          >
            {!isAuthenticated 
              ? 'Sign in to Contact Host'
              : pricing 
                ? 'Contact Host'
                : 'Select dates to contact host'
            }
          </Button>

          {/* Disclaimer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              lineHeight: 1.4,
            }}
          >
            You won't be charged yet. The host will contact you to confirm availability and payment details.
          </Typography>
        </CardContent>

        {/* Contact Host Form Modal */}
        <ContactHostForm
          listing={listing}
          isOpen={showContactForm}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
          initialData={{
            checkInDate: formData.checkInDate,
            checkOutDate: formData.checkOutDate,
            guestCount: formData.guestCount,
          }}
        />
      </Card>
    </LocalizationProvider>
  );
};

export default BookingInquiryWidget;